import React, { useCallback, useEffect, useState } from "react"
import {
	Modal,
	Button,
	Descriptions,
	Tag,
	Select,
	notification,
	Typography,
	Row,
	Col,
	Checkbox,
	Divider,
	Collapse,
	Alert,
} from "antd"
import moment from "moment"
import { Icon, DeliveryStatusMap } from "components"
import {
	getStatusColor,
	getStatusLabel,
	getExternalStatusLabel,
} from "utils/status"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { ALL_CONTRACTS_VALUE } from "hooks"
import {
	Route,
	NotDeliveredReason,
	ORDER_COMPLETION_STATUSES,
	StatusFlowEvent,
} from "../types"
import {
	sectionPanelStyle,
	getRouteStatusOptions,
	getCompletionStatusOptions,
	FieldLabel,
	StatusField,
	DeliveredDateField,
	NotDeliveredReasonField,
	TimeField,
	NotesField,
	AttachmentsField,
} from "./StatusFormFields"

const { Text } = Typography

function formatTime(d: Date): string {
	return d.toLocaleTimeString(undefined, {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	})
}

interface AddressDetailsModalProps {
	visible: boolean
	address: any
	business: string
	contract: string
	route?: Route
	setRoutes: React.Dispatch<React.SetStateAction<Route[]>>
	notDeliveredReasons?: NotDeliveredReason[]
	onClose: () => void
	hideUntreatedBoxes?: boolean
}

const AddressDetailsModal: React.FC<AddressDetailsModalProps> = ({
	visible,
	address,
	business,
	contract,
	route,
	setRoutes,
	notDeliveredReasons = [],
	onClose,
	hideUntreatedBoxes = false,
}) => {
	// "All contracts" has no single contract to scope status/override updates to.
	const isAllContracts = contract === ALL_CONTRACTS_VALUE

	const [statusFormData, setStatusFormData] = useState<any>({
		status: "",
		notDeliveredReason: "",
		deliveredDate: null,
		arrivalTime: null,
		departureTime: null,
		notes: "",
		attachments: [],
	})
	const [initialFormData, setInitialFormData] = useState<any>(null)
	const [updatingStatus, setUpdatingStatus] = useState(false)
	const [fileList, setFileList] = useState<any[]>([])
	const [pendingFiles, setPendingFiles] = useState<any[]>([])
	const [formErrors, setFormErrors] = useState<Record<string, string>>({})
	const [hasDifferentStatuses, setHasDifferentStatuses] = useState(false)
	const [alternativeStatuses, setAlternativeStatuses] = useState<
		Array<{
			id: string
			status: string
			notDeliveredReason: string
			deliveredDate: any
			arrivalTime: any
			departureTime: any
			notes: string
			orderIds: string[]
			fileList: any[]
			pendingFiles: any[]
		}>
	>([])
	const [initialAlternativeStatuses, setInitialAlternativeStatuses] = useState<
		typeof alternativeStatuses
	>([])

	// External-delivery last-resort override (finished external orders)
	const [overrideForm, setOverrideForm] = useState<any>({
		status: "",
		notes: "",
		deliveredDate: null,
		notDeliveredReason: "",
	})
	const [overrideSubmitting, setOverrideSubmitting] = useState(false)

	// Initialize status form when modal opens
	useEffect(() => {
		if (visible && address) {
			const formData = {
				status: address.order.status || "on_transit",
				notDeliveredReason: address.order.notDeliveredReason || "",
				deliveredDate: address.order.deliveredDate
					? moment(address.order.deliveredDate)
					: moment(),
				arrivalTime: address.order.arrivalTime
					? moment(address.order.arrivalTime)
					: null,
				departureTime: address.order.departureTime
					? moment(address.order.departureTime)
					: null,
				notes: address.order.notes || "",
				attachments: [],
			}
			setStatusFormData(formData)
			setInitialFormData(formData)
			setFileList([])
			setPendingFiles([])
			setFormErrors({})

			// Seed the external override form from the order's current finished state
			setOverrideForm({
				status: address.order.status || "",
				notes: "",
				deliveredDate: address.order.deliveredDate
					? moment(address.order.deliveredDate)
					: null,
				notDeliveredReason: address.order.notDeliveredReason || "",
			})

			// Check if bundled orders have different statuses
			if (
				address.order.bundledOrders &&
				address.order.bundledOrders.length > 0
			) {
				const mainStatus = address.order.status
				const statusGroups = new Map<
					string,
					{
						status: string
						notDeliveredReason: string
						deliveredDate: any
						arrivalTime: any
						departureTime: any
						notes: string
						orderIds: string[]
						fileList: any[]
						pendingFiles: any[]
					}
				>()

				// Group orders by their unique status configuration
				for (const bundledOrder of address.order.bundledOrders) {
					const orderId = bundledOrder.orderId || bundledOrder
					const orderStatus = bundledOrder.status || mainStatus

					// Only create alternative groups for orders with different status than main
					if (orderStatus !== mainStatus) {
						// Create a unique key based on status and other fields
						const key = `${orderStatus}_${bundledOrder.notDeliveredReason || ""}_${bundledOrder.deliveredDate || ""}`

						if (!statusGroups.has(key)) {
							statusGroups.set(key, {
								status: orderStatus,
								notDeliveredReason: bundledOrder.notDeliveredReason || "",
								deliveredDate: bundledOrder.deliveredDate
									? moment(bundledOrder.deliveredDate)
									: null,
								arrivalTime: bundledOrder.arrivalTime
									? moment(bundledOrder.arrivalTime)
									: null,
								departureTime: bundledOrder.departureTime
									? moment(bundledOrder.departureTime)
									: null,
								notes: bundledOrder.notes || "",
								orderIds: [],
								fileList: [],
								pendingFiles: [],
							})
						}

						statusGroups.get(key)!.orderIds.push(orderId)
					}
				}

				// Convert map to array and set state
				const altStatuses = Array.from(statusGroups.values()).map(
					(group, index) => ({
						...group,
						id: `${Date.now()}_${index}`,
						fileList: [],
						pendingFiles: [],
					})
				)

				if (altStatuses.length > 0) {
					setHasDifferentStatuses(true)
					setAlternativeStatuses(altStatuses)
					setInitialAlternativeStatuses(JSON.parse(JSON.stringify(altStatuses)))
				} else {
					setHasDifferentStatuses(false)
					setAlternativeStatuses([])
					setInitialAlternativeStatuses([])
				}
			} else {
				setHasDifferentStatuses(false)
				setAlternativeStatuses([])
				setInitialAlternativeStatuses([])
			}
		}
	}, [visible, address])

	// Validate form whenever relevant fields change
	useEffect(() => {
		const errors: Record<string, string> = {}

		// Check if both times need to be set
		if (statusFormData.arrivalTime && !statusFormData.departureTime) {
			errors.departureTime =
				Strings.routes.departureTimeRequiredWhenArrivalTimeSet
		} else if (!statusFormData.arrivalTime && statusFormData.departureTime) {
			errors.arrivalTime =
				Strings.routes.arrivalTimeRequiredWhenDepartureTimeSet
		}

		// Check if arrival is after departure
		if (statusFormData.arrivalTime && statusFormData.departureTime) {
			const arrivalMinutes =
				statusFormData.arrivalTime.hour() * 60 +
				statusFormData.arrivalTime.minute()
			const departureMinutes =
				statusFormData.departureTime.hour() * 60 +
				statusFormData.departureTime.minute()

			if (arrivalMinutes > departureMinutes) {
				errors.times = Strings.routes.arrivalTimeCannotBeAfterDepartureTime
			}
		}

		setFormErrors(errors)
	}, [statusFormData.arrivalTime, statusFormData.departureTime])

	// Check if form data has changed
	const hasFormChanged = () => {
		if (!initialFormData) return false

		// Check if files were added
		if (pendingFiles.length > 0) return true

		// Compare status
		if (statusFormData.status !== initialFormData.status) return true

		// Compare notDeliveredReason
		if (
			statusFormData.notDeliveredReason !== initialFormData.notDeliveredReason
		)
			return true

		// Compare deliveredDate
		const currentDeliveredDate = statusFormData.deliveredDate
			? moment(statusFormData.deliveredDate).format("YYYY-MM-DD HH:mm")
			: null
		const initialDeliveredDate = initialFormData.deliveredDate
			? moment(initialFormData.deliveredDate).format("YYYY-MM-DD HH:mm")
			: null
		if (currentDeliveredDate !== initialDeliveredDate) return true

		// Compare arrivalTime
		const currentArrivalTime = statusFormData.arrivalTime
			? moment(statusFormData.arrivalTime).format("HH:mm")
			: null
		const initialArrivalTime = initialFormData.arrivalTime
			? moment(initialFormData.arrivalTime).format("HH:mm")
			: null
		if (currentArrivalTime !== initialArrivalTime) return true

		// Compare departureTime
		const currentDepartureTime = statusFormData.departureTime
			? moment(statusFormData.departureTime).format("HH:mm")
			: null
		const initialDepartureTime = initialFormData.departureTime
			? moment(initialFormData.departureTime).format("HH:mm")
			: null
		if (currentDepartureTime !== initialDepartureTime) return true

		// Compare notes
		if ((statusFormData.notes || "") !== (initialFormData.notes || ""))
			return true

		// Compare alternative statuses
		if (alternativeStatuses.length !== initialAlternativeStatuses.length)
			return true

		// Deep compare alternative statuses
		for (let i = 0; i < alternativeStatuses.length; i++) {
			const current = alternativeStatuses[i]
			const initial = initialAlternativeStatuses[i]

			if (current.status !== initial.status) return true
			if (current.notDeliveredReason !== initial.notDeliveredReason) return true
			if ((current.notes || "") !== (initial.notes || "")) return true

			// Compare orderIds arrays
			if (current.orderIds.length !== initial.orderIds.length) return true
			if (
				!current.orderIds.every((id) => initial.orderIds.includes(id)) ||
				!initial.orderIds.every((id) => current.orderIds.includes(id))
			)
				return true

			// Compare deliveredDate
			const currentAltDeliveredDate = current.deliveredDate
				? moment(current.deliveredDate).format("YYYY-MM-DD HH:mm")
				: null
			const initialAltDeliveredDate = initial.deliveredDate
				? moment(initial.deliveredDate).format("YYYY-MM-DD HH:mm")
				: null
			if (currentAltDeliveredDate !== initialAltDeliveredDate) return true

			// Compare arrivalTime
			const currentAltArrivalTime = current.arrivalTime
				? moment(current.arrivalTime).format("HH:mm")
				: null
			const initialAltArrivalTime = initial.arrivalTime
				? moment(initial.arrivalTime).format("HH:mm")
				: null
			if (currentAltArrivalTime !== initialAltArrivalTime) return true

			// Compare departureTime
			const currentAltDepartureTime = current.departureTime
				? moment(current.departureTime).format("HH:mm")
				: null
			const initialAltDepartureTime = initial.departureTime
				? moment(initial.departureTime).format("HH:mm")
				: null
			if (currentAltDepartureTime !== initialAltDepartureTime) return true

			// Compare pending files
			if ((current.pendingFiles || []).length > 0) return true
		}

		return false
	}

	// Handle file upload
	const handleFileUpload = useCallback(
		async (file: any): Promise<string | null> => {
			const formData = new FormData()
			formData.append("files", file)

			try {
				const response = await API.post({
					url: Endpoints.uriImportFile(),
					data: formData,
					headers: {
						business,
						contract,
					},
				})

				if (response.ok && response.data) {
					const result = response.data?.results?.data[0]
					const imagePath = result?.url

					if (!imagePath) {
						console.error(
							"Could not extract image path from response:",
							response.data
						)
						notification.error({
							message: Strings.routes.updateFailed,
							description: "Failed to get file URL from server response",
							placement: "bottomRight",
						})
						return null
					}

					return imagePath
				}
				return null
			} catch (err) {
				console.error("File upload error:", err)
				notification.error({
					message: Strings.routes.updateFailed,
					description: Strings.routes.updateFailed,
					placement: "bottomRight",
				})
				return null
			}
		},
		[business, contract]
	)

	// Helper function to create payload for an order
	const createOrderPayload = useCallback(
		(
			orderStatus: string,
			orderData: any,
			includeAttachments: boolean = false,
			uploadedUrls: string[] = []
		) => {
			const orderFlowData: any = {
				status: orderStatus,
			}

			if (orderStatus === "delivered" && orderData.deliveredDate) {
				orderFlowData.deliveredDate = moment(orderData.deliveredDate)
					.utc()
					.toISOString()
			}

			if (orderStatus === "not_delivered") {
				orderFlowData.notDeliveredReason = orderData.notDeliveredReason
				orderFlowData.deliveredDate = null
			}

			if (orderData.notes) {
				orderFlowData.notes = orderData.notes
			}

			// Include uploaded attachments (only for main order)
			if (includeAttachments && uploadedUrls.length > 0) {
				orderFlowData.attachments = uploadedUrls.map((url: string) => ({
					url,
				}))
			}

			const payload: any = {
				orderFlow: orderFlowData,
			}

			if (orderData.arrivalTime) {
				const arrivalDateTime = moment(
					orderFlowData.deliveredDate ||
						address?.order.deliveryDate ||
						new Date()
				)
					.set({
						hour: orderData.arrivalTime.hour(),
						minute: orderData.arrivalTime.minute(),
						second: 0,
						millisecond: 0,
					})
					.utc()
					.toISOString()
				payload.arrivalTime = arrivalDateTime
			}

			if (orderData.departureTime) {
				const departureDateTime = moment(
					orderFlowData.deliveredDate ||
						address?.order.deliveryDate ||
						new Date()
				)
					.set({
						hour: orderData.departureTime.hour(),
						minute: orderData.departureTime.minute(),
						second: 0,
						millisecond: 0,
					})
					.utc()
					.toISOString()
				payload.departureTime = departureDateTime
			}

			return payload
		},
		[address]
	)

	// Validate form data for main order and alternative statuses
	const validateFormData = useCallback(() => {
		// Validation for main order
		if (
			statusFormData.status === "not_delivered" &&
			!statusFormData.notDeliveredReason
		) {
			notification.error({
				message: Strings.routes.validationError,
				description: Strings.routes.provideNotDeliveryReason,
				placement: "bottomRight",
			})
			return false
		}

		if (
			statusFormData.notDeliveredReason === "other_reason" &&
			!statusFormData.notes?.trim()
		) {
			notification.error({
				message: Strings.routes.validationError,
				description: Strings.orders.emptyNotesField,
				placement: "bottomRight",
			})
			return false
		}

		// Validation for alternative statuses
		if (hasDifferentStatuses && alternativeStatuses.length > 0) {
			for (const [index, altStatus] of alternativeStatuses.entries()) {
				// Check if orders are selected
				if (altStatus.orderIds.length === 0) {
					notification.error({
						message: Strings.routes.validationError,
						description: Strings.routes.selectAtLeastOneOrder.replace(
							"{0}",
							String(index + 1)
						),
						placement: "bottomRight",
					})
					return false
				}

				// Check not delivered reason
				if (
					altStatus.status === "not_delivered" &&
					!altStatus.notDeliveredReason
				) {
					notification.error({
						message: Strings.routes.validationError,
						description: `${Strings.routes.alternativeStatusGroup.replace("{0}", String(index + 1))}: ${Strings.routes.provideNotDeliveryReason}`,
						placement: "bottomRight",
					})
					return false
				}

				// Check delivered date
				if (altStatus.status === "delivered" && !altStatus.deliveredDate) {
					notification.error({
						message: Strings.routes.validationError,
						description: Strings.routes.provideDeliveredDate.replace(
							"{0}",
							String(index + 1)
						),
						placement: "bottomRight",
					})
					return false
				}

				// Check notes for other_reason
				if (
					altStatus.notDeliveredReason === "other_reason" &&
					!altStatus.notes?.trim()
				) {
					notification.error({
						message: Strings.routes.validationError,
						description: `${Strings.routes.alternativeStatusGroup.replace("{0}", String(index + 1))}: ${Strings.orders.emptyNotesField}`,
						placement: "bottomRight",
					})
					return false
				}
			}
		}

		// Validate if there are form errors
		if (Object.keys(formErrors).length > 0) {
			notification.error({
				message: Strings.routes.validationError,
				description: Strings.routes.fixFormErrors,
				placement: "bottomRight",
			})
			return false
		}

		return true
	}, [statusFormData, hasDifferentStatuses, alternativeStatuses, formErrors])

	// Handle status update
	const handleStatusUpdate = useCallback(async () => {
		if (!address || isAllContracts) return

		const orderId = address.order.orderId
		const optimizerOrderId = address.order.optimizerOrderId
		if (!orderId) {
			notification.error({
				message: Strings.routes.updateFailed,
				description: Strings.routes.orderIdNotFound,
				placement: "bottomRight",
			})
			return
		}

		// Validate main order and alternative statuses
		if (!validateFormData()) {
			return
		}

		setUpdatingStatus(true)

		try {
			// Upload pending files for main order
			let uploadedUrls: string[] = []
			if (pendingFiles.length > 0) {
				// Upload files sequentially instead of in parallel to avoid race conditions
				for (const file of pendingFiles) {
					const uploadedUrl = await handleFileUpload(file)

					if (uploadedUrl === null) {
						// Upload failed, stop the process
						setUpdatingStatus(false)
						return
					}

					uploadedUrls.push(uploadedUrl)
				}
			}

			// Upload pending files for alternative statuses
			const altStatusUploads: Map<string, string[]> = new Map()
			if (hasDifferentStatuses && alternativeStatuses.length > 0) {
				for (const altStatus of alternativeStatuses) {
					const altUploadedUrls: string[] = []
					if (altStatus.pendingFiles && altStatus.pendingFiles.length > 0) {
						for (const file of altStatus.pendingFiles) {
							const uploadedUrl = await handleFileUpload(file)

							if (uploadedUrl === null) {
								// Upload failed, stop the process
								setUpdatingStatus(false)
								return
							}

							altUploadedUrls.push(uploadedUrl)
						}
					}
					if (altUploadedUrls.length > 0) {
						altStatusUploads.set(altStatus.id, altUploadedUrls)
					}
				}
			}

			// Create array of all orders to update (main + bundled + bundled with different statuses)
			const ordersToUpdate: Array<{ orderId: string; payload: any }> = []

			// Add main order
			const mainOrderPayload = createOrderPayload(
				statusFormData.status,
				statusFormData,
				true,
				uploadedUrls
			)
			ordersToUpdate.push({
				orderId: orderId,
				payload: mainOrderPayload,
			})

			// Store main order payload for route update
			const orderFlowData = mainOrderPayload.orderFlow

			// Add bundled orders
			if (
				address.order.bundledOrders &&
				address.order.bundledOrders.length > 0
			) {
				// Create a map of order ID to alternative status
				const orderStatusMap = new Map<
					string,
					(typeof alternativeStatuses)[0]
				>()

				if (hasDifferentStatuses && alternativeStatuses.length > 0) {
					for (const altStatus of alternativeStatuses) {
						for (const orderId of altStatus.orderIds) {
							orderStatusMap.set(orderId, altStatus)
						}
					}
				}

				// Add all bundled orders to the update array
				for (const bundledOrder of address.order.bundledOrders) {
					const bundledOrderId = bundledOrder.orderId || bundledOrder
					const altStatus = orderStatusMap.get(bundledOrderId)

					// Use alternative status if exists, otherwise use main status
					const orderData = altStatus || statusFormData
					const orderStatus = altStatus
						? altStatus.status
						: statusFormData.status

					// Get uploaded URLs for this alternative status
					const altUrls = altStatus
						? altStatusUploads.get(altStatus.id) || []
						: []

					const bundledPayload = createOrderPayload(
						orderStatus,
						orderData,
						altStatus ? true : false,
						altUrls
					)

					ordersToUpdate.push({
						orderId: bundledOrderId,
						payload: bundledPayload,
					})
				}
			}

			// Update all orders in bulk (transaction-like behavior)
			const updatePromises = ordersToUpdate.map(({ orderId, payload }) =>
				API.put({
					url: Endpoints.uriOrders(orderId),
					data: payload,
					headers: {
						business,
						contract,
					},
				})
			)

			const results = await Promise.allSettled(updatePromises)

			// Check for failures
			const failedUpdates = results.filter(
				(result) =>
					result.status === "rejected" ||
					(result as PromiseFulfilledResult<any>).value?.ok === false
			)

			if (failedUpdates.length > 0) {
				// If main order failed (first in array), show error and don't continue
				if (
					results[0].status === "rejected" ||
					(results[0] as PromiseFulfilledResult<any>).value?.ok === false
				) {
					const errorMessage =
						results[0].status === "fulfilled"
							? (results[0] as PromiseFulfilledResult<any>).value?.data?.message
							: Strings.routes.updateError

					notification.error({
						message: Strings.routes.updateFailed,
						description: errorMessage || Strings.routes.updateError,
						placement: "bottomRight",
						duration: 5,
					})
					setUpdatingStatus(false)
					return
				}

				// Some bundled orders failed
				notification.warning({
					message: Strings.routes.partialUpdate,
					description: Strings.routes.ordersUpdatedPartially
						.replace(
							"{0}",
							String(ordersToUpdate.length - failedUpdates.length)
						)
						.replace("{1}", String(ordersToUpdate.length))
						.replace("{2}", String(failedUpdates.length)),
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				// Success notification
				notification.success({
					message: Strings.routes.statusUpdated,
					description:
						ordersToUpdate.length > 1
							? Strings.routes.ordersUpdatedSuccessfully.replace(
									"{0}",
									String(ordersToUpdate.length)
								)
							: Strings.routes.statusUpdateSuccess.replace(
									"{0}",
									statusFormData.status
								),
					placement: "bottomRight",
					duration: 3,
				})
			}

			// Update route with new address data
			setRoutes((prevRoutes) => {
				return prevRoutes.map((r) => {
					const routeMatches = r._id === route?._id
					if (!routeMatches) return r
					return {
						...r,
						addresses: r.addresses.map((addr) => {
							const addressOrderId = addr.orderId || addr.order_id
							const orderIdMatches =
								addressOrderId === orderId ||
								addressOrderId === optimizerOrderId

							// If order ID doesn't match, return address as is
							if (!orderIdMatches) {
								return addr
							}

							// Create a map of order ID to alternative status
							const orderStatusMap = new Map<
								string,
								(typeof alternativeStatuses)[0]
							>()

							if (hasDifferentStatuses && alternativeStatuses.length > 0) {
								for (const altStatus of alternativeStatuses) {
									for (const orderId of altStatus.orderIds) {
										orderStatusMap.set(orderId, altStatus)
									}
								}
							}

							// Update the matching address and bundled orders
							return {
								...addr,
								order: {
									...addr.order,
									status: statusFormData.status,
									deliveredDate:
										orderFlowData.deliveredDate || addr.order.deliveredDate,
									arrivalTime:
										mainOrderPayload.arrivalTime || addr.order.arrivalTime,
									departureTime:
										mainOrderPayload.departureTime || addr.order.departureTime,
									notes: statusFormData.notes || addr.order.notes,
									notDeliveredReason:
										statusFormData.status === "not_delivered"
											? statusFormData.notDeliveredReason
											: addr.order.notDeliveredReason,
									bundledOrders: addr.order.bundledOrders?.map(
										(bundledOrder: any) => {
											const bundledOrderId =
												bundledOrder.orderId || bundledOrder

											const altStatus = orderStatusMap.get(bundledOrderId)

											if (altStatus) {
												return {
													...bundledOrder,
													status: altStatus.status,
													notDeliveredReason:
														altStatus.status === "not_delivered"
															? altStatus.notDeliveredReason
															: bundledOrder.notDeliveredReason,
												}
											}

											// Use main status if no alternative
											return {
												...bundledOrder,
												status: statusFormData.status,
												notDeliveredReason:
													statusFormData.status === "not_delivered"
														? statusFormData.notDeliveredReason
														: bundledOrder.notDeliveredReason,
											}
										}
									),
								},
							}
						}),
					}
				})
			})

			onClose()
		} catch (err) {
			notification.error({
				message: Strings.routes.updateFailed,
				description: Strings.routes.updateError,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setUpdatingStatus(false)
		}
	}, [
		address,
		isAllContracts,
		statusFormData,
		business,
		contract,
		route,
		setRoutes,
		onClose,
		pendingFiles,
		handleFileUpload,
		hasDifferentStatuses,
		alternativeStatuses,
		validateFormData,
		createOrderPayload,
	])

	// Handle external-delivery last-resort status override
	const handleExternalOverride = useCallback(async () => {
		if (!address || isAllContracts) return

		const orderId = address.order.orderId
		const optimizerOrderId = address.order.optimizerOrderId
		if (!orderId) {
			notification.error({
				message: Strings.routes.updateFailed,
				description: Strings.routes.orderIdNotFound,
				placement: "bottomRight",
			})
			return
		}

		// Validate
		if (!overrideForm.status) {
			notification.error({
				message: Strings.routes.validationError,
				description: Strings.generic.select,
				placement: "bottomRight",
			})
			return
		}
		if (!overrideForm.notes?.trim()) {
			notification.error({
				message: Strings.routes.validationError,
				description: Strings.routes.externalOverrideNotesRequired,
				placement: "bottomRight",
			})
			return
		}
		if (overrideForm.status === "delivered" && !overrideForm.deliveredDate) {
			notification.error({
				message: Strings.routes.validationError,
				description: Strings.routes.provideDeliveredDate.replace("{0}", "1"),
				placement: "bottomRight",
			})
			return
		}
		if (
			overrideForm.status === "not_delivered" &&
			!overrideForm.notDeliveredReason
		) {
			notification.error({
				message: Strings.routes.validationError,
				description: Strings.routes.provideNotDeliveryReason,
				placement: "bottomRight",
			})
			return
		}

		setOverrideSubmitting(true)

		try {
			const payload: any = {
				status: overrideForm.status,
				notes: overrideForm.notes.trim(),
			}

			if (overrideForm.status === "delivered" && overrideForm.deliveredDate) {
				payload.deliveredDate = moment(overrideForm.deliveredDate)
					.utc()
					.toISOString()
			}
			if (overrideForm.status === "not_delivered") {
				payload.notDeliveredReason = overrideForm.notDeliveredReason
			}

			const response = await API.patch({
				url: Endpoints.v2.uriOrderExternalStatusOverride(
					address.order._id || orderId
				),
				data: payload,
				headers: {
					business,
					contract,
				},
			})

			if (!response.ok) {
				notification.error({
					message: Strings.routes.updateFailed,
					description: response?.data?.message || Strings.routes.updateError,
					placement: "bottomRight",
					duration: 5,
				})
				setOverrideSubmitting(false)
				return
			}

			notification.success({
				message: Strings.routes.statusUpdated,
				description: Strings.routes.externalOverrideSuccess.replace(
					"{0}",
					getStatusLabel(overrideForm.status)
				),
				placement: "bottomRight",
				duration: 3,
			})

			// Reflect the change locally (external data is preserved server-side)
			setRoutes((prevRoutes) =>
				prevRoutes.map((r) => {
					if (r._id !== route?._id) return r
					return {
						...r,
						addresses: r.addresses.map((addr) => {
							const addressOrderId = addr.orderId || addr.order_id
							if (
								addressOrderId !== orderId &&
								addressOrderId !== optimizerOrderId
							) {
								return addr
							}
							return {
								...addr,
								order: {
									...addr.order,
									status: overrideForm.status,
									deliveredDate:
										payload.deliveredDate || addr.order.deliveredDate,
									notDeliveredReason:
										overrideForm.status === "not_delivered"
											? overrideForm.notDeliveredReason
											: addr.order.notDeliveredReason,
								},
							}
						}),
					}
				})
			)

			onClose()
		} catch (err) {
			notification.error({
				message: Strings.routes.updateFailed,
				description: Strings.routes.updateError,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setOverrideSubmitting(false)
		}
	}, [
		address,
		isAllContracts,
		overrideForm,
		business,
		contract,
		route,
		setRoutes,
		onClose,
	])

	if (!address) return null

	const showOrderChanges =
		[...ORDER_COMPLETION_STATUSES, "on_transit"].includes(
			address.order.status
		) &&
		!address.order.restarted &&
		!address.order.externalDelivery?.partner

	const showDeliveryLocationsMap = ORDER_COMPLETION_STATUSES.includes(
		address.order.status
	)

	// Check if order is assigned to external delivery
	const isAssignedToExternal = !!address.order.externalDelivery?.partner

	// Built per-render so they reflect the active locale
	const routeStatusOptions = getRouteStatusOptions()
	const completionStatusOptions = getCompletionStatusOptions()

	return (
		<Modal
			title={`${Strings.orders.order} - ${address.order.orderId || "-"}`}
			visible={visible}
			onCancel={onClose}
			footer={null}
			width={800}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
				<Descriptions column={1} bordered size="small">
					<Descriptions.Item label={Strings.orders.orderId}>
						<a
							href={`/orders/${address.order._id || address.order.orderId || address.order_id}`}
							target="_blank"
							rel="noopener noreferrer"
							style={{
								fontSize: 13,
								fontWeight: 500,
								color: "#1C70D9",
								textDecoration: "none",
							}}
						>
							{address.order.orderId || address.order_id}
						</a>
					</Descriptions.Item>
					<Descriptions.Item label={Strings.fields.address}>
						{address.address}
					</Descriptions.Item>
					<Descriptions.Item label={Strings.routes.currentStatus}>
						<Tag color={getStatusColor(address.order.status)}>
							{getStatusLabel(address.order.status)}
						</Tag>
					</Descriptions.Item>

					{address.order.notDeliveredReason && (
						<Descriptions.Item label={Strings.routes.notDeliveredReason}>
							<Tag color="red">
								{notDeliveredReasons.find(
									(r) => r.id === address.order.notDeliveredReason
								)?.reason || address.order.notDeliveredReason}
							</Tag>
						</Descriptions.Item>
					)}
					{address.order.slot && (
						<Descriptions.Item label={Strings.routes.deliverySlot}>
							{`${Math.floor(address.order.slot.from / 3600000)
								.toString()
								.padStart(
									2,
									"0"
								)}:${(Math.floor(address.order.slot.from / 60000) % 60).toString().padStart(2, "0")}`}
							-
							{`${Math.floor(address.order.slot.to / 3600000)
								.toString()
								.padStart(
									2,
									"0"
								)}:${(Math.floor(address.order.slot.to / 60000) % 60).toString().padStart(2, "0")}`}
						</Descriptions.Item>
					)}

					<Descriptions.Item label={Strings.routes.boxes}>
						{address.order.boxes && address.order.boxes.length > 0 ? (
							address.order.boxes.map((box: any, index: number) => (
								<span
									key={index}
									style={{
										textDecoration:
											hideUntreatedBoxes && !box.treated
												? "line-through"
												: "none",
										opacity: hideUntreatedBoxes && !box.treated ? 0.6 : 1,
									}}
								>
									{box.id}
									{address.order.bagId ? ` (${address.order.bagId})` : ""}
									{index < address.order.boxes.length - 1 ? ", " : ""}
								</span>
							))
						) : (
							<Text type="secondary">{Strings.routes.noBoxesAssigned}</Text>
						)}
					</Descriptions.Item>

					{address.order.bundledOrders &&
						address.order.bundledOrders.length > 0 && (
							<Descriptions.Item label={Strings.orders.bundledOrders}>
								<div
									style={{ display: "flex", flexDirection: "column", gap: 8 }}
								>
									<Tag color="purple" style={{ width: "fit-content" }}>
										{address.order.bundledOrders.length} bundled order
										{address.order.bundledOrders.length > 1 ? "s" : ""}
									</Tag>
									<div
										style={{ display: "flex", flexDirection: "column", gap: 6 }}
									>
										{address.order.bundledOrders.map(
											(bundledOrder: any, index: number) => {
												const orderId = bundledOrder.orderId || bundledOrder
												const boxes = bundledOrder.boxes || []
												const bagId = bundledOrder.bagId

												const currentStatus =
													bundledOrder.status || address.order.status

												return (
													<div
														key={index}
														style={{
															display: "flex",
															flexDirection: "column",
															gap: 4,
															padding: "8px",
															backgroundColor: "#fafafa",
															borderRadius: "4px",
															border: "1px solid #f0f0f0",
														}}
													>
														<div
															style={{
																display: "flex",
																justifyContent: "space-between",
																alignItems: "center",
																gap: 8,
															}}
														>
															<a
																href={`/orders/${orderId}`}
																target="_blank"
																rel="noopener noreferrer"
																style={{
																	fontSize: 13,
																	fontWeight: 500,
																	color: "#1C70D9",
																	textDecoration: "none",
																	width: "fit-content",
																	display: "inline-block",
																}}
															>
																{orderId}
															</a>
															<Tag color={getStatusColor(currentStatus)}>
																{getStatusLabel(currentStatus)}
															</Tag>
														</div>
														{boxes.length > 0 && (
															<div style={{ fontSize: 12, color: "#595959" }}>
																<Text strong style={{ fontSize: 12 }}>
																	{Strings.routes.boxes}:
																</Text>{" "}
																{boxes.map((box: any, boxIdx: number) => (
																	<span key={boxIdx}>
																		{box.id}
																		{bagId ? ` (${bagId})` : ""}
																		{boxIdx < boxes.length - 1 ? ", " : ""}
																	</span>
																))}
															</div>
														)}
													</div>
												)
											}
										)}
									</div>
								</div>
							</Descriptions.Item>
						)}
				</Descriptions>

				{address.order?.externalDelivery?.partner && (
					<Collapse
						ghost
						expandIconPosition="right"
						defaultActiveKey={["externalDelivery"]}
						className="no-padding-collapse"
					>
						<Collapse.Panel
							header={
								<h4 style={{ margin: 0 }}>{Strings.routes.externalDelivery}</h4>
							}
							key="externalDelivery"
						>
							{(() => {
								const flow: StatusFlowEvent[] =
									address.order.externalDelivery.statusFlow ?? []
								const actualPickup = flow.find(
									(e) => e.status === "pickup_complete"
								)
								const actualDropoff = flow.find(
									(e) =>
										e.status === "delivered" || e.status === "not_delivered"
								)
								const ts = (t: { $date: string } | string) =>
									formatTime(new Date(typeof t === "string" ? t : t.$date))
								return (
									<Descriptions column={1} size="small" bordered>
										<Descriptions.Item
											label={Strings.routes.externalDeliveryPartner}
										>
											{address.order.externalDelivery.partner
												.charAt(0)
												.toUpperCase() +
												address.order.externalDelivery.partner.slice(1)}
										</Descriptions.Item>
										{address.order.externalDelivery.status && (
											<Descriptions.Item
												label={Strings.routes.externalDeliveryStatus}
											>
												{getExternalStatusLabel(
													address.order.externalDelivery.status
												)}
											</Descriptions.Item>
										)}
										{address.order.externalDelivery.courier?.name && (
											<Descriptions.Item
												label={Strings.routes.externalDeliveryCourier}
											>
												{address.order.externalDelivery.courier.name}
												{address.order.externalDelivery.courier.phoneNumber &&
													` · ${address.order.externalDelivery.courier.phoneNumber}`}
												{address.order.externalDelivery.courier.vehicleType &&
													` · ${address.order.externalDelivery.courier.vehicleType}`}
											</Descriptions.Item>
										)}
										{address.order.externalDelivery.pickupEta && (
											<Descriptions.Item
												label={Strings.routes.externalDeliveryPickupEta}
											>
												<Text>
													{formatTime(
														new Date(address.order.externalDelivery.pickupEta)
													)}
												</Text>
												{actualPickup && (
													<>
														{" · "}
														<Text type="secondary">
															{getExternalStatusLabel(actualPickup.status)}{" "}
															{ts(actualPickup.timestamp)}
														</Text>
													</>
												)}
											</Descriptions.Item>
										)}
										{address.order.externalDelivery.dropoffEta && (
											<Descriptions.Item
												label={Strings.routes.externalDeliveryDropoffEta}
											>
												<Text>
													{formatTime(
														new Date(address.order.externalDelivery.dropoffEta)
													)}
												</Text>
												{actualDropoff && (
													<>
														{" · "}
														<Text type="secondary">
															{getExternalStatusLabel(actualDropoff.status)}{" "}
															{ts(actualDropoff.timestamp)}
														</Text>
													</>
												)}
											</Descriptions.Item>
										)}
										{address.order.externalDelivery.trackingUrl && (
											<Descriptions.Item
												label={Strings.routes.externalDeliveryTracking}
											>
												<Button
													size="small"
													href={address.order.externalDelivery.trackingUrl}
													target="_blank"
													rel="noopener noreferrer"
												>
													{Strings.routes.externalDeliveryTracking}
												</Button>
											</Descriptions.Item>
										)}
										{address.order.externalDelivery.returnPincode && (
											<Descriptions.Item
												label={Strings.routes.externalDeliveryReturnPincode}
											>
												<Text strong>
													{address.order.externalDelivery.returnPincode}
												</Text>
											</Descriptions.Item>
										)}
										{address.order.externalDelivery.pickupVerification
											?.barcodes?.[0]?.value && (
											<Descriptions.Item
												label={Strings.routes.externalDeliveryBarcode}
											>
												<Text>
													{
														address.order.externalDelivery.pickupVerification
															.barcodes[0].value
													}
													{address.order.externalDelivery.pickupVerification
														.barcodes[0].type &&
														` · ${address.order.externalDelivery.pickupVerification.barcodes[0].type}`}
												</Text>
											</Descriptions.Item>
										)}
										{!!address.order.externalDelivery.manifestItems?.length && (
											<Descriptions.Item
												label={Strings.routes.externalDeliveryManifestItems}
											>
												{address.order.externalDelivery.manifestItems.map(
													(
														item: { name?: string; quantity?: number },
														idx: number
													) => (
														<div key={`${item.name}-${idx}`}>
															<Text>
																{item.name}
																{item.quantity && item.quantity > 1
																	? ` × ${item.quantity}`
																	: ""}
															</Text>
														</div>
													)
												)}
											</Descriptions.Item>
										)}
									</Descriptions>
								)
							})()}
						</Collapse.Panel>
					</Collapse>
				)}

				{/* Unified delivery progress: status timeline + map (internal & external) */}
				<div>
					<Collapse
						ghost
						expandIconPosition="right"
						defaultActiveKey={showDeliveryLocationsMap ? ["deliveryMap"] : []}
						className="no-padding-collapse"
					>
						<Collapse.Panel
							header={
								<h4 style={{ margin: 0 }}>{Strings.routes.deliveryProgress}</h4>
							}
							key="deliveryMap"
						>
							<DeliveryStatusMap
								isExternal={!!address.order?.externalDelivery?.partner}
								plannedLocation={address.location}
								deliveredLocation={address.order?.deliveredLocation}
								externalFlow={address.order?.externalDelivery?.statusFlow}
								reduceStatus={true}
							/>
						</Collapse.Panel>
					</Collapse>
				</div>

				{showOrderChanges && (
					<div style={sectionPanelStyle}>
						<h4 style={{ marginTop: 0, marginBottom: 12 }}>
							{Strings.routes.changeStatus}
						</h4>

						{/* Row 1: Status Selector - Full Width */}
						<Row gutter={16} style={{ marginBottom: 16 }}>
							<Col span={24}>
								<StatusField
									value={statusFormData.status}
									onChange={(value) =>
										setStatusFormData({ ...statusFormData, status: value })
									}
									options={routeStatusOptions}
								/>
							</Col>
						</Row>

						{/* Row 2: Delivered Date & Time | Not Delivered Reason */}
						<Row gutter={16} style={{ marginBottom: 16 }}>
							<Col span={12}>
								<DeliveredDateField
									status={statusFormData.status}
									value={statusFormData.deliveredDate}
									onChange={(date) =>
										setStatusFormData({
											...statusFormData,
											deliveredDate: date,
										})
									}
								/>
							</Col>
							<Col span={12}>
								<NotDeliveredReasonField
									status={statusFormData.status}
									value={statusFormData.notDeliveredReason}
									reasons={notDeliveredReasons}
									onChange={(value) =>
										setStatusFormData({
											...statusFormData,
											notDeliveredReason: value,
										})
									}
								/>
							</Col>
						</Row>

						{/* Row 3: Arrival Time | Departure Time */}
						<Row gutter={16} style={{ marginBottom: 16 }}>
							<Col span={12}>
								<TimeField
									label={Strings.routes.arrivalTime}
									value={statusFormData.arrivalTime}
									error={!!(formErrors.arrivalTime || formErrors.times)}
									onChange={(time) =>
										setStatusFormData({
											...statusFormData,
											arrivalTime: time,
										})
									}
								/>
							</Col>
							<Col span={12}>
								<TimeField
									label={Strings.routes.departureTime}
									value={statusFormData.departureTime}
									error={!!(formErrors.departureTime || formErrors.times)}
									onChange={(time) =>
										setStatusFormData({
											...statusFormData,
											departureTime: time,
										})
									}
								/>
							</Col>
							{Object.keys(formErrors).length > 0 && (
								<Col span={24} style={{ marginTop: 4 }}>
									{Object.values(formErrors).map(
										(error: string, index: number) => (
											<Text
												key={index}
												type="danger"
												style={{ fontSize: 12, display: "block" }}
											>
												{error}
											</Text>
										)
									)}
								</Col>
							)}
						</Row>

						{/* Row 4: Notes - Full Width */}
						<Row gutter={16} style={{ marginBottom: 16 }}>
							<Col span={24}>
								<NotesField
									value={statusFormData.notes}
									required={
										statusFormData.notDeliveredReason === "other_reason"
									}
									onChange={(value) =>
										setStatusFormData({
											...statusFormData,
											notes: value,
										})
									}
								/>
							</Col>
						</Row>

						{/* Row 5: Attachments - Full Width */}
						<Row gutter={16} style={{ marginBottom: 16 }}>
							<Col span={24}>
								<AttachmentsField
									fileList={fileList}
									onAdd={(file) => {
										setFileList((prev) => [...prev, file])
										setPendingFiles((prev) => [...prev, file])
									}}
									onRemove={(file) => {
										const index = fileList.indexOf(file)
										const newFileList = fileList.slice()
										newFileList.splice(index, 1)
										setFileList(newFileList)

										const newPendingFiles = pendingFiles.slice()
										newPendingFiles.splice(index, 1)
										setPendingFiles(newPendingFiles)
									}}
								/>
							</Col>
						</Row>

						{/* Different Statuses Checkbox - Only show if there are bundled orders */}
						{address.order.bundledOrders &&
							address.order.bundledOrders.length > 0 && (
								<Row gutter={16} style={{ marginBottom: 16 }}>
									<Col span={24}>
										<Checkbox
											checked={hasDifferentStatuses}
											onChange={(e) => {
												setHasDifferentStatuses(e.target.checked)
												if (
													e.target.checked &&
													alternativeStatuses.length === 0
												) {
													// Add first alternative status group
													setAlternativeStatuses([
														{
															id: Date.now().toString(),
															status: "on_transit",
															notDeliveredReason: "",
															deliveredDate: null,
															arrivalTime: null,
															departureTime: null,
															notes: "",
															orderIds: [],
															fileList: [],
															pendingFiles: [],
														},
													])
												}
											}}
										>
											{Strings.routes.differentStatusQuestion}
										</Checkbox>
									</Col>
								</Row>
							)}

						{/* Alternative Status Groups */}
						{hasDifferentStatuses && (
							<>
								<Divider style={{ margin: "16px 0" }} />
								<h4 style={{ marginBottom: 12 }}>
									{Strings.routes.alternativeStatuses}
								</h4>

								{alternativeStatuses.map((altStatus, index) => (
									<div
										key={altStatus.id}
										style={{
											marginBottom: 24,
											padding: 16,
											backgroundColor: "#ffffff",
											borderRadius: 8,
											border: "1px solid #e8e8e8",
										}}
									>
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												marginBottom: 12,
											}}
										>
											<h5 style={{ margin: 0 }}>
												{Strings.routes.alternativeStatusGroup.replace(
													"{0}",
													String(index + 1)
												)}
											</h5>
											<Button
												type="link"
												danger
												size="small"
												onClick={() => {
													setAlternativeStatuses(
														alternativeStatuses.filter(
															(s) => s.id !== altStatus.id
														)
													)
												}}
											>
												{Strings.generic.remove}
											</Button>
										</div>

										{/* Order Selection | Status */}
										<Row gutter={16} style={{ marginBottom: 12 }}>
											<Col span={12}>
												<FieldLabel required>
													{Strings.routes.selectOrders}
												</FieldLabel>
												<Select
													mode="multiple"
													style={{ width: "100%" }}
													placeholder={Strings.routes.selectOrdersPlaceholder}
													value={altStatus.orderIds}
													onChange={(value) => {
														const updated = [...alternativeStatuses]
														updated[index].orderIds = value
														setAlternativeStatuses(updated)
													}}
												>
													{address.order.bundledOrders?.map(
														(bundledOrder: any) => {
															const orderId =
																bundledOrder.orderId || bundledOrder

															// Check if this order is already selected in another group
															const isSelectedInOtherGroup =
																alternativeStatuses.some(
																	(altStat, altIndex) =>
																		altIndex !== index &&
																		altStat.orderIds.includes(orderId)
																)

															return (
																<Select.Option
																	key={orderId}
																	value={orderId}
																	disabled={isSelectedInOtherGroup}
																>
																	{orderId}
																	{isSelectedInOtherGroup &&
																		` ${Strings.routes.alreadySelected}`}
																</Select.Option>
															)
														}
													)}
												</Select>
											</Col>
											<Col span={12}>
												<StatusField
													value={altStatus.status}
													onChange={(value) => {
														const updated = [...alternativeStatuses]
														updated[index].status = value
														setAlternativeStatuses(updated)
													}}
													options={routeStatusOptions}
												/>
											</Col>
										</Row>

										{/* Delivered Date | Not Delivered Reason */}
										<Row gutter={16} style={{ marginBottom: 12 }}>
											<Col span={12}>
												<DeliveredDateField
													status={altStatus.status}
													value={altStatus.deliveredDate}
													onChange={(date) => {
														const updated = [...alternativeStatuses]
														updated[index].deliveredDate = date
														setAlternativeStatuses(updated)
													}}
												/>
											</Col>
											<Col span={12}>
												<NotDeliveredReasonField
													status={altStatus.status}
													value={altStatus.notDeliveredReason}
													reasons={notDeliveredReasons}
													onChange={(value) => {
														const updated = [...alternativeStatuses]
														updated[index].notDeliveredReason = value
														setAlternativeStatuses(updated)
													}}
												/>
											</Col>
										</Row>

										{/* Arrival Time | Departure Time */}
										<Row gutter={16} style={{ marginBottom: 12 }}>
											<Col span={12}>
												<TimeField
													label={Strings.routes.arrivalTime}
													value={altStatus.arrivalTime}
													onChange={(time) => {
														const updated = [...alternativeStatuses]
														updated[index].arrivalTime = time
														setAlternativeStatuses(updated)
													}}
												/>
											</Col>
											<Col span={12}>
												<TimeField
													label={Strings.routes.departureTime}
													value={altStatus.departureTime}
													onChange={(time) => {
														const updated = [...alternativeStatuses]
														updated[index].departureTime = time
														setAlternativeStatuses(updated)
													}}
												/>
											</Col>
										</Row>

										{/* Notes */}
										<Row gutter={16} style={{ marginBottom: 12 }}>
											<Col span={24}>
												<NotesField
													value={altStatus.notes}
													required={
														altStatus.notDeliveredReason === "other_reason"
													}
													onChange={(value) => {
														const updated = [...alternativeStatuses]
														updated[index].notes = value
														setAlternativeStatuses(updated)
													}}
												/>
											</Col>
										</Row>

										{/* Attachments */}
										<Row gutter={16}>
											<Col span={24}>
												<AttachmentsField
													fileList={altStatus.fileList || []}
													onAdd={(file) => {
														const updated = [...alternativeStatuses]
														updated[index].fileList = [
															...(updated[index].fileList || []),
															file,
														]
														updated[index].pendingFiles = [
															...(updated[index].pendingFiles || []),
															file,
														]
														setAlternativeStatuses(updated)
													}}
													onRemove={(file) => {
														const updated = [...alternativeStatuses]
														const currentFileList =
															updated[index].fileList || []
														const currentPendingFiles =
															updated[index].pendingFiles || []

														const fileIndex = currentFileList.indexOf(file)
														if (fileIndex > -1) {
															currentFileList.splice(fileIndex, 1)
															currentPendingFiles.splice(fileIndex, 1)
														}

														updated[index].fileList = currentFileList
														updated[index].pendingFiles = currentPendingFiles
														setAlternativeStatuses(updated)
													}}
												/>
											</Col>
										</Row>
									</div>
								))}

								{/* Add Another Alternative Status Button */}
								<Button
									type="dashed"
									block
									icon={<Icon name="plus" />}
									onClick={() => {
										setAlternativeStatuses([
											...alternativeStatuses,
											{
												id: Date.now().toString(),
												status: "on_transit",
												notDeliveredReason: "",
												deliveredDate: null,
												arrivalTime: null,
												departureTime: null,
												notes: "",
												orderIds: [],
												fileList: [],
												pendingFiles: [],
											},
										])
									}}
									style={{ marginBottom: 16 }}
								>
									{Strings.routes.addAnotherAlternativeStatus}
								</Button>
							</>
						)}

						<div style={{ display: "flex", justifyContent: "flex-end" }}>
							<Button
								type="primary"
								loading={updatingStatus}
								onClick={handleStatusUpdate}
								disabled={isAllContracts || !hasFormChanged()}
							>
								{Strings.routes.updateStatus}
							</Button>
						</div>
					</div>
				)}

				{isAssignedToExternal && (
					<div style={sectionPanelStyle}>
						<h4 style={{ marginTop: 0, marginBottom: 12 }}>
							{Strings.routes.externalOverrideTitle}
						</h4>
						<Alert
							type="warning"
							showIcon
							message={Strings.routes.externalOverrideWarning}
							style={{ marginBottom: 16, fontSize: 12 }}
						/>

						{/* Target status */}
						<Row gutter={16} style={{ marginBottom: 16 }}>
							<Col span={24}>
								<StatusField
									value={overrideForm.status}
									onChange={(value) =>
										setOverrideForm({ ...overrideForm, status: value })
									}
									options={completionStatusOptions}
								/>
							</Col>
						</Row>

						{/* Delivered Date | Not Delivered Reason */}
						<Row gutter={16} style={{ marginBottom: 16 }}>
							<Col span={12}>
								<DeliveredDateField
									status={overrideForm.status}
									value={overrideForm.deliveredDate}
									onChange={(date) =>
										setOverrideForm({ ...overrideForm, deliveredDate: date })
									}
								/>
							</Col>
							<Col span={12}>
								<NotDeliveredReasonField
									status={overrideForm.status}
									value={overrideForm.notDeliveredReason}
									reasons={notDeliveredReasons}
									onChange={(value) =>
										setOverrideForm({
											...overrideForm,
											notDeliveredReason: value,
										})
									}
								/>
							</Col>
						</Row>

						{/* Notes (required) */}
						<Row gutter={16} style={{ marginBottom: 16 }}>
							<Col span={24}>
								<NotesField
									value={overrideForm.notes}
									required
									onChange={(value) =>
										setOverrideForm({ ...overrideForm, notes: value })
									}
								/>
							</Col>
						</Row>

						<div style={{ display: "flex", justifyContent: "flex-end" }}>
							<Button
								type="primary"
								loading={overrideSubmitting}
								onClick={handleExternalOverride}
								disabled={
									isAllContracts ||
									!overrideForm.status ||
									!overrideForm.notes?.trim()
								}
							>
								{Strings.routes.externalOverrideSubmit}
							</Button>
						</div>
					</div>
				)}
			</div>
		</Modal>
	)
}

export default AddressDetailsModal
